import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import CircleXmark from '@gravity-ui/icons/CircleXmark';
import FileArrowUp from '@gravity-ui/icons/FileArrowUp';
import FileQuestion from '@gravity-ui/icons/FileQuestion';
import { Alert, Flex, Icon, SegmentedRadioGroup, useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { installableAppUploadImage } from 'client/api';
import { Field } from 'client/blocks/form/Field/Field';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { FormErrors } from 'client/hocs/types';
import { InstallableAppFormSettings } from 'client/types';
import { translateError } from 'client/utils/errorMessage';
import { ACCEPTED_LOGO_FORMATS } from 'client/utils/settings/common';
import * as appFormKeyset from 'i18n/AppForm';
import * as keyset from 'i18n/LogoUploader';
import * as settingsKeyset from 'i18n/Settings';

import styles from './FilesUploaders.module.scss';

const COVER_DOCS_URL = 'https://yandex.ru/dev/dialogs/apps/doc/ru/store-requirements';
const COVER_UPLOAD_TOAST = 'COVER_UPLOAD_TOAST';

const LogoPreview = {
    WithText: 'WithText',
    WithIcon: 'WithIcon',
} as const;

type TLogoPreview = (typeof LogoPreview)[keyof typeof LogoPreview];

interface IconUploaderProps {
    disabled: boolean;
    logoUrl: string | null | undefined;

    onIdChange?: (logoUrl: string) => void;
    onUrlChange?: (logoUrl: string) => void;
    setErrors?: (errors: FormErrors<InstallableAppFormSettings & object>, cb?: () => void) => void;
}

interface IconPreviewProps {
    logoUrl?: string | null;
    previewType: TLogoPreview;
}

const t = i18n(keyset);
const tSettings = i18n(settingsKeyset);
const tAppForm = i18n(appFormKeyset);

const checkAlertDescription = {
    [LogoPreview.WithText]: t('check-cover-with-text-description'),
    [LogoPreview.WithIcon]: t('check-cover-with-icon-description'),
};

const createAppLogo = (isLoading: boolean, isDragActive: boolean, userFileName: string | undefined) => {
    const containerStyle = [styles.appIcon__container, styles.appIcon_empty, styles.appIcon_largeBorderStroke];

    if (isLoading)
        return (
            <div className={clx(...containerStyle, styles.appIcon_loading)}>
                <Icon data={FileQuestion} size={15} />
                <div className={clx(styles.appIcon__label, styles.appIcon__label_large)}>
                    {tSettings('icon-is-loading')}
                </div>
            </div>
        );

    if (isDragActive)
        return (
            <div className={clx(...containerStyle)}>
                <div>{tAppForm('load-here')}</div>
            </div>
        );

    if (userFileName)
        return (
            <div className={clx(...containerStyle)}>
                <div>
                    {userFileName.length > 30 ? `...${userFileName.slice(userFileName.length - 27)}` : userFileName}
                </div>
            </div>
        );

    return (
        <div className={clx(...containerStyle)}>
            <Icon data={FileArrowUp} size={15} />
            <div className={clx(styles.appIcon__label, styles.appIcon__label_large)}>
                {'770x436'}
                <br />
                {tSettings('icon-click-to-load')}
            </div>
        </div>
    );
};

const Logo: FC<IconPreviewProps> = ({ logoUrl, previewType }) => {
    return (
        <div
            className={clx(styles.logoPreview__container)}
            style={logoUrl ? { backgroundImage: `url(${logoUrl})` } : {}}
        >
            <div className={clx(styles.accentArea, styles.accentArea_darkgray)}>
                {previewType === LogoPreview.WithIcon && <div className={styles.accentArea__logoAccent} />}
            </div>
        </div>
    );
};

export const InstallableAppLogoUploader: FC<IconUploaderProps> = ({
    disabled,
    logoUrl,
    onIdChange,
    onUrlChange,
    setErrors,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [previewType, setPreviewType] = useState<TLogoPreview>(LogoPreview.WithText);
    const [userFileName, setUserFileName] = useState<string>();
    const { aliceApp } = useContext(CurrentAliceAppContext)!;

    const { add: addToast } = useToaster();

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;
        if (userFileName) {
            timer = setTimeout(() => {
                setUserFileName(undefined);
            }, 1500);
        }
        return () => clearTimeout(timer);
    }, [userFileName]);

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (!acceptedFiles.length) return;

            const file = acceptedFiles[0];
            setIsLoading(true);

            installableAppUploadImage(aliceApp.id, aliceApp.channel, file, 'appTileImage')
                .then((image) => {
                    setUserFileName(file.name);
                    onIdChange && onIdChange(image.id);
                    onUrlChange && onUrlChange(image.url);
                    setIsLoading(false);
                })
                .catch((err) => {
                    const { message } = err;
                    const translatedErrorMessage = translateError(message);
                    setErrors && setErrors({ logoId: translatedErrorMessage });
                    addToast({
                        title: t('logo-uploader-error'),
                        content: translatedErrorMessage,
                        theme: 'danger',
                        renderIcon: CircleXmark,
                        name: COVER_UPLOAD_TOAST,
                    });
                    setUserFileName(undefined);
                    setIsLoading(false);
                });
        },
        [aliceApp.id, aliceApp.channel, onIdChange, onUrlChange, setErrors, addToast],
    );
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        disabled,
        accept: ACCEPTED_LOGO_FORMATS,
    });

    return (
        <div className={styles.coverUploaderContainer}>
            <Field
                required
                fieldTitle={t('cover-and-icon-title')}
                fieldDescription={t('cover-and-icon-description')}
                containerClassName={styles.coverUploaderFormContainer}
                contentClassName={styles.coverUploaderFormContent}
            >
                <Alert
                    theme="info"
                    title={<span className={styles.logoUploaderAlert__title}>{t('cover-requirement-title')}</span>}
                    message={
                        <span className={styles.logoUploaderAlert__message}>{t('cover-requirement-description')}</span>
                    }
                    actions={
                        <Flex>
                            <Alert.Action onClick={() => window.open(COVER_DOCS_URL, '_blank')}>
                                {t('cover-requirement-action')}
                            </Alert.Action>
                        </Flex>
                    }
                />
            </Field>
            <Field
                required
                fieldTitle={t('cover-upload-title')}
                fieldDescription={t('cover-upload-description')}
                containerClassName={clx(
                    styles.coverUploaderFormContainer,
                    styles.coverUploaderFormContainer_minMarginBottom,
                )}
                contentClassName={styles.coverUploaderFormContent}
            >
                <SegmentedRadioGroup
                    className={styles.selectLogoPreviewRadio}
                    options={[
                        { value: LogoPreview.WithText, content: t('logo-uploader-with-text-option') },
                        { value: LogoPreview.WithIcon, content: t('logo-uploader-with-icon-option') },
                    ]}
                    onUpdate={(value) => setPreviewType(value)}
                    value={previewType}
                    size="l"
                />
                <Alert
                    theme="info"
                    title={<span className={styles.logoUploaderAlert__title}>{t('check-cover-title')}</span>}
                    message={
                        <span className={styles.logoUploaderAlert__message}>{checkAlertDescription[previewType]}</span>
                    }
                />
            </Field>
            <div className={styles.logoUploaderContainer}>
                {!disabled && (
                    <div
                        className={clx(styles.logoDropzone, disabled && styles.logoDropzone_disabled)}
                        {...getRootProps()}
                    >
                        <input {...getInputProps()} />
                        {createAppLogo(isLoading, isDragActive, userFileName)}
                    </div>
                )}
                <Logo logoUrl={logoUrl} previewType={previewType} />
            </div>
        </div>
    );
};
